import React, { useCallback, useEffect, useState } from 'react';

import {
  Alert,
  Button,
  Card,
  Col,
  Pagination,
  Row,
  Spinner,
  Table
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAngleRight,
  faChevronLeft,
  faChevronRight
} from '@fortawesome/free-solid-svg-icons';

import {
  UilEye,
  UilRefresh,
  UilEdit,
  UilTrash
} from '@iconscout/react-unicons';

import { toast } from 'react-toastify';

import FoodDetailsModal from 'components/modals/FoodDetailsModal';
import ConfirmDeleteModal from 'components/modals/ConfirmDeleteModal';

import {
  Food,
  FoodPagination,
  getFoodById,
  getFoods,
  deleteFood
} from 'services/foodService';

import FoodFilter, {
  FoodFilterValues
} from 'components/modules/food/FoodFilter';
import { useNavigate } from 'react-router-dom';
import usePagination from 'hooks/usePagination';

const PAGE_SIZE = 20;

const FoodList: React.FC = () => {
  const navigate = useNavigate();
  const [foods, setFoods] = useState<Food[]>([]);
  const [isAllVisible, setIsAllVisible] = useState(false);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);

  const [deletingFoodId, setDeletingFoodId] = useState<number | null>(null);

  const [deleting, setDeleting] = useState(false);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [foodToDelete, setFoodToDelete] = useState<Food | null>(null);

  const [pagination, setPagination] = useState<FoodPagination>({
    page: 1,
    limit: PAGE_SIZE,
    total: 0,
    totalPages: 0
  });

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState<string | null>(null);

  const [showViewModal, setShowViewModal] = useState(false);

  const [selectedFood, setSelectedFood] = useState<Food | null>(null);

  const [loadingDetails, setLoadingDetails] = useState(false);

  const [filters, setFilters] = useState<FoodFilterValues>({
    food_name: '',
    brand_name: ''
  });

  /*
   * Load foods
   *
   * Filters are passed to the API so
   * filtering happens on the backend.
   */
  const loadFoods = useCallback(
    async (
      page: number,
      currentFilters: FoodFilterValues = filters,
      currentLimit: number = pageSize
    ) => {
      setLoading(true);
      setError(null);

      try {
        const response = await getFoods(page, currentLimit, {
          food_name: currentFilters.food_name || undefined,

          brand_name: currentFilters.brand_name || undefined
        });

        setFoods(response.foods || []);

        setPagination({
          ...response.pagination,
          page: Number(response.pagination.page),
          limit: Number(response.pagination.limit),
          total: Number(response.pagination.total),
          totalPages: Number(response.pagination.totalPages)
        });
      } catch (err: any) {
        const message = err?.message || 'Failed to load foods.';

        setError(message);

        toast.error(message);
      } finally {
        setLoading(false);
      }
    },
    [filters, pageSize]
  );

  const { hasNextEllipsis, hasPrevEllipsis, visiblePaginationItems } =
    usePagination({
      currentPageNo: pagination.page || 1,
      totalPage: pagination.totalPages || 1,
      maxPaginationButtonCount: 5
    });

  /*
   * Initial load
   */
  useEffect(() => {
    loadFoods(1, filters);
  }, [loadFoods]);

  /*
   * Filter input change
   */
  const handleFilterChange = (field: keyof FoodFilterValues, value: string) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  /*
   * Search
   */
  const handleSearch = () => {
    loadFoods(1, filters);
  };

  /*
   * Clear filters
   */
  const handleClear = () => {
    const clearedFilters: FoodFilterValues = {
      food_name: '',
      brand_name: ''
    };

    setFilters(clearedFilters);

    loadFoods(1, clearedFilters);
  };

  /*
   * Previous page
   */
  const handlePrevious = () => {
    if (pagination.page <= 1) {
      return;
    }

    loadFoods(pagination.page - 1, filters);
  };

  /*
   * Next page
   */
  const handleNext = () => {
    if (pagination.page >= pagination.totalPages) {
      return;
    }

    loadFoods(Number(pagination.page) + 1, filters);
  };

  /*
   * Refresh
   */
  const handleRefresh = () => {
    loadFoods(pagination.page, filters);
  };

  const handleToggleViewAll = () => {
    const nextIsAllVisible = !isAllVisible;
    const nextLimit = nextIsAllVisible
      ? Math.max(pagination.total, PAGE_SIZE)
      : PAGE_SIZE;

    setIsAllVisible(nextIsAllVisible);
    setPageSize(nextLimit);
    loadFoods(1, filters, nextLimit);
  };

  const handleGoToPage = (page: number) => {
    if (page < 1 || page > (pagination.totalPages || 1) || loading) {
      return;
    }

    loadFoods(page, filters);
  };

  const handleDeleteClick = (food: Food) => {
    setFoodToDelete(food);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!foodToDelete) {
      return;
    }

    try {
      setDeleting(true);
      setDeletingFoodId(foodToDelete.id);

      await deleteFood(foodToDelete.id);

      toast.success(`Food "${foodToDelete.name_en}" deleted successfully.`);

      /*
       * Reload current page.
       *
       * If the last item on the page
       * was deleted, go to previous page.
       */

      const currentPage = pagination.page;

      const currentTotal = pagination.total;

      const newTotal = currentTotal - 1;

      const newTotalPages = Math.ceil(newTotal / pagination.limit);

      const pageToLoad =
        currentPage > newTotalPages && newTotalPages > 0
          ? newTotalPages
          : currentPage;

      await loadFoods(pageToLoad, filters);

      setShowDeleteConfirm(false);
      setFoodToDelete(null);
    } catch (error: any) {
      const message = error?.message || 'Failed to delete food.';

      toast.error(message);
    } finally {
      setDeleting(false);
      setDeletingFoodId(null);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setFoodToDelete(null);
  };

  /*
   * View food
   */
  const handleViewFood = async (foodId: number) => {
    setShowViewModal(true);
    setSelectedFood(null);
    setLoadingDetails(true);

    try {
      const food = await getFoodById(foodId);

      setSelectedFood(food);
    } catch (err: any) {
      const message = err?.message || 'Failed to load food details.';

      toast.error(message);

      setShowViewModal(false);
    } finally {
      setLoadingDetails(false);
    }
  };

  /*
   * Close details modal
   */
  const handleCloseViewModal = () => {
    setShowViewModal(false);
    navigate('/superadmin/food');
  };

  /*
   * Format number
   */
  const formatNumber = (value: number | string | null | undefined) => {
    if (value === null || value === undefined || value === '') {
      return '-';
    }

    return Number(value).toFixed(2);
  };

  return (
    <div className="mb-9">
      <Row className="g-3">
        {/* Header */}
        <Col xs={12}>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h3 className="mb-1">Food Database</h3>

              <p className="text-body-secondary mb-0">
                Manage foods available in the MLAS food database.
              </p>
            </div>

            <div className="d-flex align-items-center gap-2">
              {/* Add Food */}
              <Button
                variant="primary"
                type="button"
                onClick={() => navigate('/superadmin/food/add')}
              >
                Add Food
              </Button>

              {/* Refresh */}
              <Button
                variant="outline-primary"
                type="button"
                onClick={handleRefresh}
                disabled={loading}
              >
                {loading ? (
                  <Spinner animation="border" size="sm" className="me-2" />
                ) : (
                  <UilRefresh size={18} className="me-1" />
                )}
                Refresh
              </Button>
            </div>
          </div>
        </Col>

        {/* Error */}
        {error && (
          <Col xs={12}>
            <Alert variant="danger" className="mb-0">
              {error}
            </Alert>
          </Col>
        )}

        {/* Filters */}
        <Col xs={12}>
          <FoodFilter
            filters={filters}
            onChange={handleFilterChange}
            onSearch={handleSearch}
            onClear={handleClear}
            loading={loading}
          />
        </Col>

        {/* Food table */}
        <Col xs={12}>
          <Card>
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h5 className="mb-1">Foods</h5>

                  <span className="text-body-secondary">
                    Total foods: {pagination.total}
                  </span>
                </div>

                <span className="text-body-secondary">
                  Page {pagination.page} of {pagination.totalPages || 1}
                </span>
              </div>
            </Card.Header>

            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table hover className="mb-0 align-middle">
                  <thead>
                    <tr>
                      <th
                        className="ps-4"
                        style={{
                          minWidth: '300px'
                        }}
                      >
                        Food
                      </th>

                      <th
                        style={{
                          minWidth: '160px'
                        }}
                      >
                        Brand
                      </th>

                      <th
                        style={{
                          minWidth: '100px'
                        }}
                      >
                        Calories (kcal)
                      </th>

                      <th
                        style={{
                          minWidth: '100px'
                        }}
                      >
                        Protein (g)
                      </th>

                      <th
                        style={{
                          minWidth: '100px'
                        }}
                      >
                        Fat (g)
                      </th>

                      <th
                        style={{
                          minWidth: '110px'
                        }}
                      >
                        Carbs (g)
                      </th>

                      <th
                        className="text-center"
                        style={{
                          minWidth: '100px'
                        }}
                      >
                        Action
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {loading && foods.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-center py-5">
                          <Spinner animation="border" />
                        </td>
                      </tr>
                    ) : foods.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-center py-5">
                          No foods found.
                        </td>
                      </tr>
                    ) : (
                      foods.map(food => (
                        <tr key={food.id}>
                          {/* Food */}
                          <td className="ps-4">
                            <div>
                              <strong>{food.name_en}</strong>

                              {food.name_ar && (
                                <div
                                  className="text-body-secondary small mt-1"
                                  dir="rtl"
                                  style={{ textAlign: 'left' }}
                                >
                                  {food.name_ar}
                                </div>
                              )}
                            </div>
                          </td>

                          {/* Brand */}
                          <td className="ps-4">
                            <div>
                              <strong>{food.brand?.name_en || '-'}</strong>

                              {food.name_ar && (
                                <div
                                  className="text-body-secondary small mt-1"
                                  dir="rtl"
                                  style={{ textAlign: 'left' }}
                                >
                                  {food.brand?.name_ar}
                                </div>
                              )}
                            </div>
                          </td>

                          {/* Calories */}
                          <td>
                            <strong>
                              {formatNumber(food.nutrition?.calories)}
                            </strong>
                          </td>

                          {/* Protein */}
                          <td>
                            {formatNumber(food.nutrition?.protein_g)}{' '}
                            {food.nutrition?.protein_g !== null &&
                            food.nutrition?.protein_g !== undefined
                              ? 'g'
                              : ''}
                          </td>

                          {/* Fat */}
                          <td>
                            {formatNumber(food.nutrition?.fat_g)}{' '}
                            {food.nutrition?.fat_g !== null &&
                            food.nutrition?.fat_g !== undefined
                              ? 'g'
                              : ''}
                          </td>

                          {/* Carbs */}
                          <td>
                            {formatNumber(food.nutrition?.carbohydrates_g)}{' '}
                            {food.nutrition?.carbohydrates_g !== null &&
                            food.nutrition?.carbohydrates_g !== undefined
                              ? 'g'
                              : ''}
                          </td>

                          {/* Actions */}
                          <td>
                            <div className="d-flex align-items-center gap-1">
                              {/* View */}
                              <Button
                                variant="outline-primary"
                                size="sm"
                                className="px-2 py-1"
                                onClick={() => {
                                  navigate(`/superadmin/food/${food.id}`);
                                  handleViewFood(food.id);
                                }}
                              >
                                View
                              </Button>

                              {/* Edit */}
                              <Button
                                variant="outline-warning"
                                size="sm"
                                className="px-2 py-1"
                                onClick={() =>
                                  navigate(`/superadmin/food/${food.id}/edit`)
                                }
                              >
                                Edit
                              </Button>

                              {/* Delete */}
                              <Button
                                variant="outline-danger"
                                size="sm"
                                className="px-2 py-1"
                                onClick={() => handleDeleteClick(food)}
                                disabled={
                                  deleting && deletingFoodId === food.id
                                }
                              >
                                {deleting && deletingFoodId === food.id ? (
                                  <Spinner
                                    animation="border"
                                    size="sm"
                                    className="me-1"
                                  />
                                ) : (
                                  'Delete'
                                )}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>

            {/* Pagination */}
            <Card.Footer>
              <Row className="align-items-center py-1 g-2">
                <Col className="d-flex fs-9 align-items-center">
                  <p className="mb-0 d-none d-sm-block me-3 fw-semibold text-body">
                    {foods.length > 0
                      ? (pagination.page - 1) * pagination.limit + 1
                      : 0}{' '}
                    to{' '}
                    {foods.length > 0
                      ? Math.min(
                          pagination.page * pagination.limit,
                          pagination.total
                        )
                      : 0}
                    <span className="text-body-tertiary"> items of </span>
                    {pagination.total}
                  </p>
                  <Button
                    variant="link"
                    className="p-0 fw-semibold"
                    onClick={handleToggleViewAll}
                    disabled={loading || pagination.total === 0}
                  >
                    View {isAllVisible ? 'less' : 'all'}
                    <FontAwesomeIcon
                      icon={faAngleRight}
                      className="ms-1 fs-9"
                    />
                  </Button>
                </Col>

                <Col xs="auto">
                  <Pagination className="mb-0 justify-content-center">
                    <Pagination.Prev
                      disabled={loading || pagination.page <= 1}
                      onClick={() => handleGoToPage(pagination.page - 1)}
                    >
                      <FontAwesomeIcon icon={faChevronLeft} />
                    </Pagination.Prev>

                    {hasPrevEllipsis && (
                      <>
                        <Pagination.Item
                          active={pagination.page === 1}
                          onClick={() => handleGoToPage(1)}
                        >
                          1
                        </Pagination.Item>
                        <Pagination.Ellipsis disabled />
                      </>
                    )}

                    {visiblePaginationItems.map(page => (
                      <Pagination.Item
                        key={page}
                        active={pagination.page === page}
                        onClick={() => handleGoToPage(page)}
                      >
                        {page}
                      </Pagination.Item>
                    ))}

                    {hasNextEllipsis && (
                      <>
                        <Pagination.Ellipsis disabled />
                        <Pagination.Item
                          active={pagination.page === pagination.totalPages}
                          onClick={() => handleGoToPage(pagination.totalPages)}
                        >
                          {pagination.totalPages}
                        </Pagination.Item>
                      </>
                    )}

                    <Pagination.Next
                      disabled={
                        loading || pagination.page >= pagination.totalPages
                      }
                      onClick={() => handleGoToPage(pagination.page + 1)}
                    >
                      <FontAwesomeIcon icon={faChevronRight} />
                    </Pagination.Next>
                  </Pagination>
                </Col>
              </Row>
            </Card.Footer>
          </Card>
        </Col>
      </Row>

      {/* Food Details Modal */}
      <FoodDetailsModal
        show={showViewModal}
        food={selectedFood}
        loading={loadingDetails}
        onHide={handleCloseViewModal}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        show={showDeleteConfirm}
        title="Delete Food"
        message={`Are you sure you want to delete "${
          foodToDelete?.name_en || 'this food'
        }"? This action cannot be undone.`}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        loading={deleting}
      />
    </div>
  );
};

export default FoodList;
